import { Hand, MapPin } from 'lucide-react';
import { Button, Chip, Description, EmptyState, Heading, Table } from '@heroui/react';
import type { ParkingSpot } from '../../../api/condominiums';
import type { AvailableSpot } from '../../../api/available-spots';
import { hasFloorPlanLocation } from '../../../components/floor-plan/floor-plan-utils';
import { getParkingSpotStatusLabel } from '../../admin/utils/parking-spot-status';
import { ParkingSpotCard, type ParkingSpotChipColor } from './parking-spot-card';

interface OccupiedSpotsSectionProps {
  allSpots: ParkingSpot[];
  availableSpots: AvailableSpot[];
  mySpotIds: Set<string>;
  onViewLocation: (spot: ParkingSpot) => void;
  onRequest: (spot: ParkingSpot) => void;
}

function statusChipColor(status: string): ParkingSpotChipColor {
  switch (status) {
    case 'OCCUPIED':
      return 'danger';
    case 'BORROWED':
      return 'success';
    case 'RENTED':
      return 'accent';
    default:
      return 'default';
  }
}

function formatLocation(spot: ParkingSpot): string {
  return [spot.level, spot.location].filter(Boolean).join(' · ') || 'Sem localização';
}

export function OccupiedSpotsSection({
  allSpots,
  availableSpots,
  mySpotIds,
  onViewLocation,
  onRequest,
}: OccupiedSpotsSectionProps) {
  const availableIds = new Set(availableSpots.map((s) => s.id));
  const occupied = allSpots
    .filter((spot) => !availableIds.has(spot.id))
    .sort((a, b) => Number(mySpotIds.has(a.id)) - Number(mySpotIds.has(b.id)));

  if (occupied.length === 0) {
    return (
      <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
        <Heading level={3}>Nenhuma vaga ocupada</Heading>
        <Description>
          Todas as vagas do condomínio estão disponíveis no período filtrado.
        </Description>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <Table aria-label="Vagas ocupadas">
          <Table.ScrollContainer>
            <Table.Content>
              <Table.Header>
                <Table.Column isRowHeader>Código</Table.Column>
                <Table.Column>Local</Table.Column>
                <Table.Column>Status</Table.Column>
                <Table.Column className="text-right">Ação</Table.Column>
              </Table.Header>
              <Table.Body items={occupied}>
                {(spot) => (
                  <Table.Row id={spot.id}>
                    <Table.Cell>
                      <span className="font-mono font-medium">{spot.code}</span>
                    </Table.Cell>
                    <Table.Cell className="text-muted">{formatLocation(spot)}</Table.Cell>
                    <Table.Cell>
                      <Chip size="sm" variant="soft" color={statusChipColor(spot.status)}>
                        {getParkingSpotStatusLabel(spot.status)}
                      </Chip>
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {hasFloorPlanLocation(spot) ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onPress={() => onViewLocation(spot)}
                          >
                            <MapPin size={14} />
                            Ver no mapa
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant={mySpotIds.has(spot.id) ? 'secondary' : 'primary'}
                          isDisabled={mySpotIds.has(spot.id)}
                          onPress={() => onRequest(spot)}
                        >
                          <Hand size={14} />
                          {mySpotIds.has(spot.id) ? 'Sua vaga' : 'Solicitar'}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        {occupied.map((spot) => (
          <ParkingSpotCard
            key={spot.id}
            code={spot.code}
            description={formatLocation(spot)}
            chipLabel={getParkingSpotStatusLabel(spot.status)}
            chipColor={statusChipColor(spot.status)}
            onViewLocation={
              hasFloorPlanLocation(spot) ? () => onViewLocation(spot) : undefined
            }
            primaryAction={{
              label: mySpotIds.has(spot.id) ? 'Sua vaga' : 'Solicitar uso',
              onPress: () => onRequest(spot),
              isDisabled: mySpotIds.has(spot.id),
              variant: mySpotIds.has(spot.id) ? 'secondary' : 'primary',
            }}
          />
        ))}
      </div>
    </>
  );
}
